<template>
  <Panel class="mb-4 items-panel" header="Transaction Items">
    <template #header>
      <div class="flex align-items-center">
        <i class="pi pi-shopping-cart mr-2"></i>
        <span>Transaction Items</span>
      </div>
    </template>

    <div class="cart-list">
      <!-- Items List -->
      <div
        v-for="(item, index) in items"
        :key="index"
        :class="[
          'cart-item flex align-items-start gap-3 py-3 border-bottom-1 surface-border',
          getItemErrors(item).length > 0 ? 'warning' : '',
        ]"
      >
        <div class="image-wrapper" @click="showImageDialog(item)" role="button">
          <img
            v-if="item.image_preview || item.image_url"
            :src="item.image_preview || item.image_url || ''"
            alt="Item Image"
            class="thumbnail"
          />
          <div v-else class="thumbnail placeholder flex align-items-center justify-content-center">
            <i class="pi pi-image text-400 text-2xl"></i>
          </div>
        </div>

        <div class="flex-1">
          <div class="flex align-items-start justify-content-between mb-2">
            <div>
              <div class="text-900 font-medium text-lg">
                {{ getCommodityName(item.commodity_type_id) || item.commodity_name || 'Unknown' }}
              </div>
              <div class="text-500">
                <Inplace v-if="!readonly">
                  <template #display>
                    Price: {{ formatCurrency(item.price_per_lb || 0) }} / lb
                  </template>
                  <template #content="{ closeCallback }">
                    <div class="flex align-items-center gap-2">
                      <InputNumber
                        v-model="item.price_per_lb"
                        :min="0"
                        :max="999.99"
                        :step="0.01"
                        :min-fraction-digits="2"
                        :max-fraction-digits="2"
                        prefix="$"
                        autofocus
                        class="w-8rem"
                      />
                      <span class="text-500">/ lb</span>
                      <Button
                        icon="pi pi-check"
                        text
                        severity="success"
                        size="small"
                        @click="closeCallback"
                        aria-label="Save"
                      />
                      <Button
                        icon="pi pi-times"
                        text
                        severity="danger"
                        size="small"
                        @click="closeCallback"
                        aria-label="Cancel"
                      />
                    </div>
                  </template>
                </Inplace>
                <span v-else>Price: {{ formatCurrency(item.price_per_lb || 0) }} / lb</span>
              </div>
            </div>
            <div class="text-right">
              <div class="text-500">Gross: {{ formatWeight(item.weight?.gross || 0) }}</div>
              <div class="text-500">Tare: {{ formatWeight(item.weight?.tare || 0) }}</div>
              <div class="text-700 font-medium">Net: {{ formatWeight(item.weight?.net || 0) }}</div>
            </div>
          </div>

          <!-- Inline validation errors -->
          <div v-if="getItemErrors(item).length > 0" class="mb-2 p-2 border-round">
            <div class="text-xs text-orange-700">
              <ul class="m-0 pl-3">
                <li v-for="err in getItemErrors(item)" :key="err">{{ err }}</li>
              </ul>
            </div>
          </div>

          <div class="flex align-items-center justify-content-between mt-1">
            <div v-if="!readonly" class="flex align-items-center gap-2">
              <Button
                icon="pi pi-pencil"
                class="p-button-text p-button-rounded p-button-sm"
                @click="$emit('edit-item', index)"
                aria-label="Edit Item"
              />
              <Button
                icon="pi pi-trash"
                class="p-button-text p-button-rounded p-button-sm p-button-danger"
                @click="$emit('remove-item', index)"
                aria-label="Remove Item"
              />
            </div>
            <div class="text-right ml-auto">
              <div class="text-900 font-semibold text-lg">
                {{ formatCurrency(calculateLineTotal(item)) }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Summary -->
      <div v-if="items.length > 0" class="flex align-items-center justify-content-between pt-3">
        <span class="text-900 font-medium">Total Items: {{ items.length }}</span>
        <span class="text-900 font-bold">{{ formatCurrency(totalAmount) }}</span>
      </div>

      <!-- Add Item Area - Always visible at bottom -->
      <div
        v-if="!readonly"
        class="add-item-area flex flex-column align-items-center py-4 border-2 border-dashed surface-border border-round cursor-pointer hover:surface-100 transition-colors transition-duration-150"
        @click="$emit('add-item')"
        role="button"
        tabindex="0"
        @keydown.enter="$emit('add-item')"
        @keydown.space.prevent="$emit('add-item')"
      >
        <i class="pi pi-cart-plus text-4xl text-400 mb-2"></i>
        <p class="text-500 m-0">Click to add an item</p>
      </div>
    </div>
  </Panel>

  <!-- Image Dialog -->
  <Dialog
    v-model:visible="imageDialogVisible"
    :style="{ width: '350px' }"
    header="Item Image"
    :modal="true"
    :closable="true"
  >
    <div class="flex justify-content-center">
      <img
        v-if="selectedItem && (selectedItem.image_preview || selectedItem.image_url)"
        :src="selectedItem.image_preview || selectedItem.image_url || ''"
        alt="Item Image"
        class="w-full max-h-64 object-contain"
      />
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import Panel from 'primevue/panel'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import Inplace from 'primevue/inplace'
import InputNumber from 'primevue/inputnumber'
import type { TransactionItem } from '@/types/Transaction'
import type { CommodityType } from '@/types/CommodityType'

const props = defineProps({
  items: {
    type: Array as () => TransactionItem[],
    required: true,
  },
  commodityTypes: {
    type: Array as () => CommodityType[],
    required: true,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits<{
  (e: 'add-item'): void
  (e: 'edit-item', index: number): void
  (e: 'remove-item', index: number): void
  (
    e: 'validate',
    payload: { valid: boolean; invalidIndexes: number[]; errorsByIndex: string[][] },
  ): void
}>()

const imageDialogVisible = ref(false)
const selectedItem = ref<TransactionItem | null>(null)

function showImageDialog(item: TransactionItem) {
  selectedItem.value = item
  imageDialogVisible.value = true
}

function hasImage(item: TransactionItem) {
  return !!(item.image_preview || item.image_url)
}

function isPositiveNumber(val: unknown) {
  const n = typeof val === 'number' ? val : Number(val)
  return Number.isFinite(n) && n > 0
}

function getItemErrors(item: TransactionItem): string[] {
  const errors: string[] = []
  if (!hasImage(item)) errors.push('Image is required')
  const gross = item.weight?.gross
  const tare = item.weight?.tare
  const net = item.weight?.net
  if (!isPositiveNumber(gross)) errors.push('Gross weight must be a positive number')
  if (!isPositiveNumber(net)) errors.push('Net weight must be a positive number')
  if (!isPositiveNumber(item.price_per_lb)) errors.push('Price per lb must be a positive number')
  return errors
}

function getCommodityName(commodityId: string): string {
  const commodity = props.commodityTypes.find((c) => c.id === commodityId)
  return commodity ? commodity.name : ''
}

function calculateLineTotal(item: TransactionItem): number {
  if (item.total_price) return item.total_price
  const price = Number(item.price_per_lb) || 0
  const weight = Number(item.weight?.net) || 0
  return parseFloat((price * weight).toFixed(2))
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value)
}

function formatWeight(value: number): string {
  return `${value.toFixed(2)} lbs`
}

const totalAmount = computed(() => {
  return props.items.reduce((total, item) => total + calculateLineTotal(item), 0)
})

function computeValidation() {
  const invalidIndexes: number[] = []
  const errorsByIndex: string[][] = []
  props.items.forEach((item, idx) => {
    const errs = getItemErrors(item)
    if (errs.length > 0) {
      invalidIndexes.push(idx)
      errorsByIndex[idx] = errs
    }
  })
  emit('validate', {
    valid: invalidIndexes.length === 0 && props.items.length > 0,
    invalidIndexes,
    errorsByIndex,
  })
}

computeValidation()

watch(
  () => props.items,
  () => computeValidation(),
  { deep: true },
)
</script>

<style scoped>
.cart-item .thumbnail {
  width: 96px;
  height: 96px;
  object-fit: cover;
  border-radius: 6px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

.cart-item .thumbnail.placeholder {
  background: #f3f4f6;
}

.image-wrapper {
  cursor: pointer;
}

/* Warning item styling */
.cart-item.warning {
  border-left: 4px solid #f59e0b !important; /* orange-500 */
  padding-left: 0.75rem;
}

/* Panel styling to match PurchaseWizardView */
.items-panel .p-panel-header {
  border-bottom: 2px solid #e5e7eb;
  background-color: #f9fafb;
  color: #374151;
  font-weight: 600;
}

.items-panel .p-panel-header span {
  font-size: 1.5rem;
}

/* Add item area styling */
.add-item-area {
  margin-top: 1rem;
  transition: all 0.15s ease;
}

.add-item-area:hover {
  border-color: var(--primary-color) !important;
  background-color: var(--primary-50) !important;
}

.add-item-area:focus {
  outline: 2px solid var(--primary-color);
  outline-offset: 2px;
}

@media screen and (max-width: 768px) {
  .cart-item .thumbnail {
    width: 72px;
    height: 72px;
  }
}
</style>
